import { forwardRef } from "react";
import type { ChangeEventHandler, ReactNode } from "react";

type BaseFieldProps = {
  label: ReactNode;
  name: string;
  helperText?: string;
  error?: string;
  required?: boolean;
};

export function FormField({
  label,
  helperText,
  error,
  required,
  children,
}: BaseFieldProps & { children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium leading-tight text-[color:var(--label-ink)]">
        {label}
        {required ? (
          <>
            {" "}
            <span className="text-[color:var(--error-500)]">*</span>
          </>
        ) : null}
      </span>
      {children}
      {error ? (
        <span
          className="text-[11px] font-normal leading-snug text-[color:var(--error-500)]"
          aria-live="polite"
        >
          {error}
        </span>
      ) : helperText ? (
        <span className="text-[11px] font-normal leading-snug text-[color:var(--text-muted)]">
          {helperText}
        </span>
      ) : null}
    </label>
  );
}

export const TextInput = forwardRef<
  HTMLInputElement,
  {
    name: string;
    type?: string;
    defaultValue?: string;
    value?: string;
    placeholder?: string;
    required?: boolean;
    hasError?: boolean;
    /** Id of the element describing this field — normally the error message.
     *  `hasError` alone only tells a screen reader that the value is invalid;
     *  this is what lets it read out WHY. */
    describedBy?: string;
    disabled?: boolean;
    min?: number | string;
    max?: number | string;
    step?: number | string;
    maxLength?: number;
    autoComplete?: string;
    autoFocus?: boolean;
    autoCapitalize?: "none" | "on" | "characters" | "words" | "sentences";
    dir?: "ltr" | "rtl" | "auto";
    inputMode?:
      | "none"
      | "text"
      | "tel"
      | "url"
      | "email"
      | "numeric"
      | "decimal"
      | "search";
    enterKeyHint?:
      | "enter"
      | "done"
      | "go"
      | "next"
      | "previous"
      | "search"
      | "send";
    className?: string;
    /** Decoration rendered absolutely on the leading edge (e.g. search icon). */
    prefix?: ReactNode;
    /** Decoration rendered absolutely on the trailing edge (e.g. verified badge, eye toggle). */
    suffix?: ReactNode;
    /** Override the auto-padding when the leading slot is wider than default (32px). */
    prefixSpace?: number;
    /** Override the auto-padding when the trailing slot is wider than default (32px). */
    suffixSpace?: number;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: ChangeEventHandler<HTMLInputElement>;
  }
>(function TextInput(
  {
    name,
    type = "text",
    defaultValue,
    value,
    placeholder,
    required,
    hasError,
    describedBy,
    disabled,
    min,
    max,
    step,
    maxLength,
    autoComplete,
    autoFocus,
    autoCapitalize,
    dir,
    inputMode,
    enterKeyHint,
    className,
    prefix,
    suffix,
    prefixSpace = 32,
    suffixSpace = 32,
    onChange,
    onBlur,
  },
  ref,
) {
  const isControlled = value !== undefined;
  const input = (
    <input
      ref={ref}
      name={name}
      type={type}
      defaultValue={isControlled ? undefined : defaultValue}
      value={value}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      maxLength={maxLength}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      autoCapitalize={autoCapitalize}
      dir={dir}
      inputMode={inputMode}
      enterKeyHint={enterKeyHint}
      aria-invalid={hasError ? "true" : undefined}
      aria-describedby={describedBy}
      onChange={onChange}
      onBlur={onBlur}
      style={{
        paddingInlineStart: prefix ? prefixSpace : undefined,
        paddingInlineEnd: suffix ? suffixSpace : undefined,
      }}
      className={`h-[35px] w-full rounded-md border bg-[color:var(--surface)] px-3 text-[13px] focus:outline-none ${
        hasError
          ? "border-[color:var(--error-500)] focus:border-[color:var(--error-500)]"
          : "border-[color:var(--border-light)] focus:border-[color:var(--primary-500)]"
      } ${disabled ? "cursor-not-allowed bg-[color:var(--background-muted)] text-[color:var(--text-muted)]" : ""} ${className ?? ""}`}
    />
  );
  if (!prefix && !suffix) return input;
  return (
    <div className="relative">
      {input}
      {prefix ? (
        <span className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3 text-[color:var(--text-muted)]">
          {prefix}
        </span>
      ) : null}
      {suffix ? (
        <span className="absolute inset-y-0 end-0 flex items-center pe-2 text-[color:var(--text-muted)]">
          {suffix}
        </span>
      ) : null}
    </div>
  );
});

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  {
    name: string;
    defaultValue?: string;
    value?: string;
    placeholder?: string;
    required?: boolean;
    hasError?: boolean;
    dir?: "ltr" | "rtl" | "auto";
    rows?: number;
    maxLength?: number;
    onChange?: ChangeEventHandler<HTMLTextAreaElement>;
    onBlur?: ChangeEventHandler<HTMLTextAreaElement>;
  }
>(function TextArea(
  {
    name,
    defaultValue,
    value,
    placeholder,
    required,
    hasError,
    dir,
    rows = 4,
    maxLength,
    onChange,
    onBlur,
  },
  ref,
) {
  const isControlled = value !== undefined;
  return (
    <textarea
      ref={ref}
      name={name}
      defaultValue={isControlled ? undefined : defaultValue}
      value={value}
      placeholder={placeholder}
      required={required}
      dir={dir}
      maxLength={maxLength}
      aria-invalid={hasError ? "true" : undefined}
      onChange={onChange}
      onBlur={onBlur}
      rows={rows}
      className={`min-h-[84px] w-full rounded-md border bg-[color:var(--surface)] px-3 py-2.5 text-[13px] focus:outline-none ${
        hasError
          ? "border-[color:var(--error-500)] focus:border-[color:var(--error-500)]"
          : "border-[color:var(--border-light)] focus:border-[color:var(--primary-500)]"
      }`}
    />
  );
});

export function ToggleInput({
  name,
  defaultChecked,
  checked,
  value,
  disabled,
  onChange,
  onBlur,
  ariaLabel,
}: {
  name: string;
  defaultChecked?: boolean;
  checked?: boolean;
  value?: string;
  disabled?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLInputElement>;
  /**
   * Accessible name for the toggle. Required when the toggle is rendered
   * without a visible associated label (e.g. inside a card footer).
   */
  ariaLabel?: string;
}) {
  return (
    <label className="relative inline-flex h-6 w-11 cursor-pointer items-center">
      <input
        name={name}
        type="checkbox"
        defaultChecked={defaultChecked}
        checked={checked}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        aria-label={ariaLabel}
        className="peer sr-only"
      />
      <span className="h-6 w-11 rounded-full bg-[color:var(--border-light)] transition-colors peer-checked:bg-[color:var(--accent-500)] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[color:var(--accent-500)] peer-disabled:cursor-not-allowed peer-disabled:opacity-60" />
      <span className="pointer-events-none absolute start-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5 peer-checked:bg-[color:var(--on-accent)] rtl:peer-checked:-translate-x-5" />
    </label>
  );
}
